/* eslint-disable max-lines */
import {
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  TableCellsIcon,
  XCircleIcon
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import useFetch from "../../hooks/useFetch";
import {
  IMarkSentResponse,
  IOperatorInvoice,
  IOperatorInvoiceOperator,
  OperatorInvoiceStatus
} from "../../interfaces/endpoints/IOperatorInvoice";
import { OperatorsSelectList } from "../../interfaces/endpoints/IOperators";
import {
  cancelOperatorInvoice,
  downloadOperatorInvoiceFile,
  getOperatorInvoices,
  GetInvoicesParamsType,
  markOperatorInvoiceSent,
  TRACKER_SHEET_URL,
  updateOperatorInvoiceDocSheet
} from "../../services/OperatorInvoice.service";
import { getAllSelectOperators } from "../../services/Operators.service";
import toast from "react-hot-toast";
import Button from "../ui/Button";
import DeleteModal from "../ui/DeleteModal";
import IconBtn from "../ui/IconBtn";
import Select from "../ui/select/Select";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import Can from "../users/permissions/Can";
import useCan from "../users/permissions/useCan";
import GenerateInvoiceForm from "./GenerateInvoiceForm";
import InvoiceStatusBadge from "./InvoiceStatusBadge";
import { IAxiosResponse } from "../../interfaces/IAxiosResponse";
import { resolveError } from "../../utils/apiErrorMessage";

const PAGE_SIZE = 20;

const columns: (string | number)[] = [
  "Operatori",
  "Periudha",
  "Statusi",
  "Total",
  "Komisioni",
  "Anulime",
  "Neto",
  "Data",
  "Veprimet"
];

const statusOptions: { id: OperatorInvoiceStatus; name: string }[] = [
  { id: OperatorInvoiceStatus.Generated, name: "Generated" },
  { id: OperatorInvoiceStatus.Sent, name: "Sent" },
  { id: OperatorInvoiceStatus.Cancelled, name: "Cancelled" }
];

const formatMoney = (value: number) => `${(value ?? 0).toFixed(2)} EUR`;

const OperatorInvoiceReport: FC = () => {
  const [operatorFilter, setOperatorFilter] =
    useState<OperatorsSelectList | null>(null);
  const [statusFilter, setStatusFilter] =
    useState<{ id: OperatorInvoiceStatus; name: string } | null>(null);
  const [operators, setOperators] = useState<OperatorsSelectList[]>([]);
  const [page, setPage] = useState(1);
  const actionErrorRef = useRef<string | null>(null);
  const lastMarkSentRef = useRef<IMarkSentResponse | null>(null);

  useEffect(() => {
    let alive = true;
    void getAllSelectOperators({ pageIndex: 1, pageSize: 100000 })
      .then((res) => {
        if (alive) setOperators(res.data?.result ?? []);
      })
      .catch(() => {
        /* fallback to #id in render */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [operatorFilter, statusFilter]);

  const operatorNames = useMemo(
    () =>
      operators.reduce<Record<number, string>>((acc, op) => {
        if (op.operatorName) acc[op.id] = op.operatorName;
        return acc;
      }, {}),
    [operators]
  );

  const params: GetInvoicesParamsType = {
    ...(operatorFilter && { operatorId: operatorFilter.id }),
    ...(statusFilter && { statusId: statusFilter.id }),
    pageIndex: page,
    pageSize: PAGE_SIZE
  };

  const { response, loading, refetch } = useFetch(
    getOperatorInvoices,
    {
      initRequest: true,
      params,
      resetResponseOnError: true
    }
  );

  const rows = response?.data?.result ?? [];
  const totalCount = response?.data?.totalCount ?? 0;
  const onPageChange = ({ selected }: { selected: number }) =>
    setPage(selected + 1);

  const handleMarkSent = async (
    id: number
  ): Promise<IAxiosResponse<IMarkSentResponse>> => {
    try {
      const res = await markOperatorInvoiceSent(id);
      lastMarkSentRef.current = res.data;
      const url = res.data?.driveFolderUrl;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast(
          (t) => (
            <div className="text-sm">
              <p className="font-medium mb-1">
                Invoice marked as sent.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => toast.dismiss(t.id)}
                className="text-primary underline"
              >
                Hap Drive folder
              </a>
              {res.data.emailSent === false && (
                <p className="mt-2 text-red-600">
                  Email s&apos;u dergua automatikisht. Njofto operatorin
                  manualisht.
                </p>
              )}
            </div>
          ),
          { duration: 10000, position: "top-right" }
        );
      }
      refetch();
      return res;
    } catch (err) {
      actionErrorRef.current = resolveError(
        err,
        "Ndodhi nje gabim gjate ndryshimit te statusit."
      );
      throw err;
    }
  };

  const handleCancel = async (
    id: number
  ): Promise<IAxiosResponse<IOperatorInvoice>> => {
    try {
      const res = await cancelOperatorInvoice(id);
      refetch();
      return res;
    } catch (err) {
      actionErrorRef.current = resolveError(
        err,
        "Ndodhi nje gabim gjate anulimit te fatures."
      );
      throw err;
    }
  };

  const canUpdate = useCan("accounting.invoicereport.update");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [trackingId, setTrackingId] = useState<number | null>(null);

  const handleUpdateDocSheet = async (row: IOperatorInvoice) => {
    setTrackingId(row.id);
    try {
      await updateOperatorInvoiceDocSheet(row.id);
      toast.success("Shtuar ne tracker.", { position: "top-right" });
      refetch();
    } catch (err) {
      toast.error(
        resolveError(err, "Ndodhi nje gabim gjate shtimit ne tracker."),
        { position: "top-right" }
      );
    } finally {
      setTrackingId(null);
    }
  };

  const handleDownload = async (row: IOperatorInvoice) => {
    setDownloadingId(row.id);
    try {
      const { blob, filename } = await downloadOperatorInvoiceFile(row.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Ndodhi nje gabim gjate shkarkimit.";
      toast.error(message, { position: "top-right" });
    } finally {
      setDownloadingId(null);
    }
  };

  const renderOperatorName = (id: number) =>
    operatorNames[id] || <span className="text-gray-400 text-xs">#{id}</span>;

  // A combined invoice carries >1 per-operator subtotal line. Single-operator invoices
  // (and legacy responses without the operators array) render exactly as before.
  const isCombined = (row: IOperatorInvoice) => (row.operators?.length ?? 0) > 1;

  // Operator cell: single name as today, or the list of covered operators stacked.
  const renderOperator = (row: IOperatorInvoice) => {
    if (!isCombined(row)) return renderOperatorName(row.operatorId);
    return (
      <div className="flex flex-col gap-0.5">
        {(row.operators ?? []).map((op) => (
          <span key={op.operatorId}>
            {op.operatorNameSnapshot || renderOperatorName(op.operatorId)}
          </span>
        ))}
      </div>
    );
  };

  // Money cell: combined value on top, with per-operator sub-lines for combined invoices.
  const renderMoneyCell = (
    row: IOperatorInvoice,
    total: number,
    pick: (op: IOperatorInvoiceOperator) => number,
    accent?: boolean
  ) => {
    const top = accent ? (
      <span className="font-semibold">{formatMoney(total)}</span>
    ) : (
      formatMoney(total)
    );
    if (!isCombined(row)) return top;
    return (
      <div className="flex flex-col gap-0.5">
        <span>{top}</span>
        {(row.operators ?? []).map((op) => (
          <span key={op.operatorId} className="text-xs text-gray-400">
            {formatMoney(pick(op))}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Can permissions="accounting.invoicereport.create">
        <GenerateInvoiceForm onGenerated={refetch} operators={operators} />
      </Can>

      <div className="flex flex-col md:flex-row gap-3 items-end">
        <div className="w-full md:w-72">
          <Select
            key={`operators-${operators.length}`}
            options={operators}
            searchFunc={(o, search) =>
              !search ||
              (o.operatorName ?? "")
                .toLocaleLowerCase()
                .startsWith(search.toLocaleLowerCase())
            }
            onSelectedItemChange={setOperatorFilter}
            selectedItem={operatorFilter}
            label="Filtro sipas operatorit"
            placeholder="Te gjithe operatoret"
            getOptionLabel={(o) => o.operatorName || `#${o.id}`}
            itemToString={(o) => o?.operatorName || ""}
          />
        </div>
        <div className="w-full md:w-48">
          <Select
            options={statusOptions}
            onSelectedItemChange={setStatusFilter}
            selectedItem={statusFilter}
            label="Filtro sipas statusit"
            placeholder="Te gjitha"
            getOptionLabel={(o) => o.name}
            itemToString={(o) => o?.name || ""}
          />
        </div>
        {(operatorFilter || statusFilter) && (
          <Button
            btnType="custom"
            className="border border-gray-300"
            onClick={() => {
              setOperatorFilter(null);
              setStatusFilter(null);
            }}
          >
            Pastro filtrat
          </Button>
        )}
      </div>

      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {rows.length ? (
          rows.map((row: IOperatorInvoice) => (
              <Row
                key={row.id}
                operator={renderOperator(row)}
                period={
                  <span className="text-xs">
                    {dayjs.utc(row.periodFrom).format("DD MMM YYYY")} →{" "}
                    {dayjs.utc(row.periodTo).format("DD MMM YYYY")}
                  </span>
                }
                status={<InvoiceStatusBadge status={row.statusId} />}
                totalPrice={renderMoneyCell(row, row.totalPrice, (op) => op.totalPrice)}
                totalGjirafaCommission={renderMoneyCell(
                  row,
                  row.totalGjirafaCommission,
                  (op) => op.totalGjirafaCommission
                )}
                totalCancellations={renderMoneyCell(
                  row,
                  row.totalCancellations,
                  (op) => op.totalCancellations
                )}
                netBillable={renderMoneyCell(
                  row,
                  row.netBillable,
                  (op) => op.netBillable,
                  true
                )}
                insertDate={
                  row.insertDate
                    ? dayjs(row.insertDate).format("DD-MM-YY")
                    : "-"
                }
                actions={
                  <span className="flex items-center gap-1">
                    <span title="Shkarko xlsx">
                      <IconBtn
                        className="w-6 h-6"
                        onClick={() => {
                          if (downloadingId !== row.id) {
                            void handleDownload(row);
                          }
                        }}
                      >
                        <ArrowDownTrayIcon
                          className={`w-4 h-4 ${
                            downloadingId === row.id ? "animate-pulse" : ""
                          }`}
                        />
                      </IconBtn>
                    </span>
                    {canUpdate &&
                      row.statusId === OperatorInvoiceStatus.Generated && (
                        <DeleteModal
                          params={row.id}
                          title="Konfirmo Mark as Sent"
                          paragraph={
                            "Pas konfirmimit, Drive folder per operatorin " +
                            "krijohet/gjendet dhe statusi behet Sent."
                          }
                          handleDelete={handleMarkSent}
                          successMessage="Mark as Sent u krye."
                          errorMessage="Deshtoi Mark as Sent"
                          onSuccess={() =>
                            lastMarkSentRef.current?.emailSent === false
                              ? "Mark as Sent u krye — njofto operatorin manualisht."
                              : "Mark as Sent u krye."
                          }
                          onError={() => actionErrorRef.current || undefined}
                          loadingMessage="Duke ndryshuar..."
                          indicator={
                            <span title="Mark as Sent">
                              <IconBtn className="w-6 h-6 text-green-600">
                                <PaperAirplaneIcon className="w-4 h-4" />
                              </IconBtn>
                            </span>
                          }
                        />
                      )}
                    {canUpdate &&
                      row.docSheetUpdatedAtUtc === null &&
                      row.statusId !== OperatorInvoiceStatus.Cancelled && (
                        <a
                          href={TRACKER_SHEET_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            if (trackingId !== row.id) {
                              void handleUpdateDocSheet(row);
                            }
                          }}
                          title="Shto ne tracker sheet"
                        >
                          <IconBtn as="span" className="w-6 h-6 text-blue-600">
                            <TableCellsIcon
                              className={`w-4 h-4 ${
                                trackingId === row.id ? "animate-pulse" : ""
                              }`}
                            />
                          </IconBtn>
                        </a>
                      )}
                    {canUpdate &&
                      row.statusId !== OperatorInvoiceStatus.Cancelled && (
                        <DeleteModal
                          params={row.id}
                          title="Anulo faturen"
                          paragraph="Do te releasohen anulimet per t'u perfshire ne gjenerimin e ardhshem."
                          handleDelete={handleCancel}
                          successMessage="Fatura u anulua."
                          errorMessage="Deshtoi anulimi"
                          onError={() => actionErrorRef.current || undefined}
                          loadingMessage="Duke anuluar..."
                          indicator={
                            <span title="Anulo">
                              <IconBtn className="w-6 h-6 text-red-600">
                                <XCircleIcon className="w-4 h-4" />
                              </IconBtn>
                            </span>
                          }
                        />
                      )}
                  </span>
                }
              />
            ))
        ) : (
          <></>
        )}
      </Table>
      <Pagination
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        page={page}
        length={rows.length}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default OperatorInvoiceReport;
